import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { NotificationResponse } from '../clients/rust-api-client';
import LoadingSpinner from '../LoadingSpinner';
import ShowAlert from '../ShowAlert';
import { GroupedList } from '../shell/GroupedList';
import { FilterBar } from '../violation/linear/FilterBar';
import {
  entityPath,
  fetchAllNotifications,
  hoaOptions,
  isStaffNotification,
  notificationTypeLabel,
  typeOptions,
} from './notificationMeta';
import {
  StaffNotificationFilters,
  applyStaffFilters,
  defaultStaffFilters,
  groupByReadState,
  recipientOptions,
} from './staffNotificationMeta';
import { getUserIdFromToken } from '../utils/TokenUtils';
import { useSyncedStaffNotifications } from '../sync/hooks';
import { setNotificationsRead } from '../sync/writes';
import { formatRelativeTime, formatTimestamp } from '../utils/dateUtils';

/**
 * The management company's own notifications (#624): rows addressed to a
 * staff user, such as a homeowner replying to your citation. What was
 * sent to homeowners is in Communications (#623).
 */
const NotificationListComponent: React.FC = () => {
  const navigate = useNavigate();
  const myUserId = useMemo(() => getUserIdFromToken(), []);
  const [fetched, setRows] = useState<NotificationResponse[]>([]);
  // From the sync engine's copy when there is one (#561): staff rows are
  // the half of `notifications` it carries, so nothing is paged through
  // and split here, and a homeowner's reply appears as it arrives.
  const synced = useSyncedStaffNotifications();
  const haveSynced = synced !== null;
  const rows = synced ?? fetched;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<StaffNotificationFilters>(() =>
    defaultStaffFilters(myUserId)
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchRows = useCallback(async (): Promise<void> => {
    if (haveSynced) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const all = await fetchAllNotifications();
      setRows(all.filter(isStaffNotification));
      setError(null);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError("Couldn't load notifications. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [haveSynced]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const visible = useMemo(
    () => applyStaffFilters(rows, filters),
    [rows, filters]
  );
  const groups = useMemo(() => groupByReadState(visible), [visible]);

  const recipients = useMemo(() => {
    const options = recipientOptions(rows, myUserId);
    // Keep "Me" choosable (and the default chip legible) when none of
    // the rows are yours yet.
    return myUserId && !options.some((o) => o.value === myUserId)
      ? [{ value: myUserId, label: 'Me' }, ...options]
      : options;
  }, [rows, myUserId]);

  const markSelected = async (isRead: boolean): Promise<void> => {
    const ids = Array.from(selected);
    try {
      setIsUpdating(true);
      // Through the write queue: the rows change at once, and change back
      // if the server refuses.
      const pending = setNotificationsRead(ids, isRead);
      setSelected(new Set());
      await pending;
      setRows((prev) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: isRead } : n))
      );
    } catch (err) {
      console.error('Error updating notifications:', err);
      setError("Couldn't update the selected notifications.");
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Straight to what it is about — the violation, the work order, the
   * reservation — and read, as opening it means. Only a notification about
   * nothing in particular opens its own page.
   */
  const open = (n: NotificationResponse): void => {
    if (!n.is_read) {
      setRows((prev) => prev.map((r) => (r.id === n.id ? { ...r, is_read: true } : r)));
      // Not awaited: a failure only leaves it unread for next time.
      setNotificationsRead([n.id], true).catch((err) =>
        console.error('Error marking the notification read:', err)
      );
    }
    navigate(entityPath(n) ?? `/notifications/${n.id}`);
  };

  // With a copy there is nothing to wait for (#635).
  if (loading && !haveSynced) {
    return <LoadingSpinner />;
  }

  const actionClass =
    'rounded-md border border-line px-2 py-1 text-[12px] font-medium text-fg-secondary hover:bg-fg/5 disabled:opacity-50';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Flush against the panel's edges, alerts need their own inset. */}
      <div className="px-3 pt-3 empty:hidden">
        {error && (
          <ShowAlert
            alertType="error"
            alertTitle="Error"
            alertMessage={error}
            isVisible={true}
            onClose={() => setError(null)}
            timeout={5000}
            isInline={true}
          />
        )}
      </div>

      <FilterBar
        fields={[
          {
            field: 'Recipient',
            options: recipients,
            values: filters.recipients,
            onChange: (recipients) => setFilters((f) => ({ ...f, recipients })),
          },
          {
            field: 'HOA',
            options: hoaOptions(rows),
            values: filters.hoas,
            onChange: (hoas) => setFilters((f) => ({ ...f, hoas })),
          },
          {
            field: 'Type',
            options: typeOptions(rows),
            values: filters.types,
            onChange: (types) => setFilters((f) => ({ ...f, types })),
          },
        ]}
        search={filters.search}
        onSearch={(search) => setFilters((f) => ({ ...f, search }))}
        searchLabel="Search notifications by title, message or recipient"
        onClearAll={() =>
          setFilters((f) => ({ ...f, recipients: [], hoas: [], types: [] }))
        }
        shown={visible.length}
        total={rows.length}
        trailing={
          selected.size > 0 && (
            <>
              <button
                type="button"
                className={actionClass}
                disabled={isUpdating}
                onClick={() => markSelected(true)}
              >
                Mark {selected.size} read
              </button>
              <button
                type="button"
                className={actionClass}
                disabled={isUpdating}
                onClick={() => markSelected(false)}
              >
                Mark unread
              </button>
            </>
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <GroupedList
          groups={groups}
          getKey={(n) => n.id}
          rowLabel={(n) => `${n.title}, for ${n.recipient_name}`}
          onOpen={open}
          selection={{ selected, onChange: setSelected }}
          emptyMessage={
            rows.length === 0
              ? 'No notifications for your team yet.'
              : 'No notifications match these filters.'
          }
          renderRow={(n) => (
            <>
              <span
                className={clsx(
                  'h-2 w-2 shrink-0 rounded-full',
                  n.is_read ? 'bg-transparent' : 'bg-accent'
                )}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate">
                <span className={clsx('text-fg', !n.is_read && 'font-medium')}>
                  {n.title}
                </span>
                <span className="ml-2 text-fg-muted">{n.message}</span>
              </span>
              <span className="hidden rounded-full border border-line px-2 py-0.5 text-[12px] text-fg-muted md:inline">
                {notificationTypeLabel(n.notification_type)}
              </span>
              <span className="hidden w-36 truncate text-[12px] text-fg-muted lg:inline">
                {n.hoa_name}
              </span>
              <span className="hidden w-32 truncate text-[12px] text-fg-muted sm:inline">
                {n.user_id === myUserId ? 'Me' : n.recipient_name}
              </span>
              <span
                className="hidden min-w-24 shrink-0 whitespace-nowrap text-right text-[12px] tabular-nums text-fg-muted sm:inline"
                title={formatTimestamp(n.created_at)}
              >
                {formatRelativeTime(n.created_at)}
              </span>
            </>
          )}
        />
      </div>
    </div>
  );
};

export default NotificationListComponent;
